import { useEffect, useRef, useState } from 'react';
import { gql, useApolloClient, useMutation } from '@apollo/client';
import { stringHelpers } from '@wistia/vhs';
import { useUploadInfo } from './useUploadInfo';
import * as tus from 'tus-js-client';
import md5 from 'md5';
import { sslEmbedHost } from '~/constantDefinitions';
import { useCrossRouteUnsavedChanges } from '~/singlePageApp/contexts/CrossRouteUnsavedChanges';
import { CURRENT_UPLOAD_LIST } from './UploadProvider';
import { distribution } from '~/singlePageApp/utilities/statsd';
import { getExtensionByFileType } from '~/utilities/file';

const CREATE_UPLOAD_DURATION = gql`
  mutation createUploadDuration($input: CreateUploadDurationInput!) {
    createUploadDuration(input: $input) {
      uploadDuration {
        id
      }
    }
  }
`;

export function getSpeed(uploadStarted, bytesUploaded, initialBytesUploaded = 0) {
  const timeElapsed = Date.now() - uploadStarted;
  const bytesUploadedInCurrentSession = bytesUploaded - initialBytesUploaded;
  return bytesUploadedInCurrentSession / (timeElapsed / 1000);
}

export function getETA(uploadStarted, bytesUploaded, bytesTotal, initialBytesUploaded = 0) {
  const uploadSpeed = getSpeed(uploadStarted, bytesUploaded, initialBytesUploaded);
  const bytesRemaining = bytesTotal - bytesUploaded;
  return Math.round((bytesRemaining / uploadSpeed) * 10) / 10;
}

function getMbPerSecond(fileSize, totalUploadTimeSeconds) {
  const fileSizeInMB = fileSize / (1024 * 1024);
  return fileSizeInMB / totalUploadTimeSeconds;
}

function getTotalUploadTimeInMilliseconds(startMilliseconds, endMilliseconds) {
  return endMilliseconds - startMilliseconds;
}

function parseAndDecodeGeolocationData(geoData) {
  if (!geoData) {
    return null;
  }
  const parsedGeoData = geoData.split(', ');

  const geoDataArray = {};
  try {
    for (let i = 0; i < parsedGeoData.length; i++) {
      const [key, value] = parsedGeoData[i].split(' ');
      geoDataArray[key] = atob(value);
    }
    return geoDataArray;
  } catch (e) {
    return null;
  }
}

function createUploadDurationParams(
  uploadStartedAt,
  uploadEndedAt,
  lastResponse,
  fileId,
  parallelUploadChunkCount,
  totalUploadTimeMilliseconds,
) {
  const uploadStartedAtISO = new Date(uploadStartedAt).toISOString();
  const uploadEndedAtISO = new Date(uploadEndedAt).toISOString();
  const geoLocationData = lastResponse.getHeader('X-Gdata');
  const geoDataArray = parseAndDecodeGeolocationData(geoLocationData);
  if (!geoDataArray) {
    return {
      variables: {
        input: {
          bakeryHashedId: fileId,
          duration: totalUploadTimeMilliseconds,
          parallelChunkCount: parallelUploadChunkCount,
          startedAt: uploadStartedAtISO,
          endedAt: uploadEndedAtISO,
        },
      },
    };
  }
  const latitude = parseFloat(geoDataArray.clt);
  const longitude = parseFloat(geoDataArray.clg);
  const isp = geoDataArray.cisp;
  const connectionType = geoDataArray.cct;
  return {
    variables: {
      input: {
        bakeryHashedId: fileId,
        duration: totalUploadTimeMilliseconds,
        parallelChunkCount: parallelUploadChunkCount,
        connectionType,
        isp,
        latitude,
        longitude,
        startedAt: uploadStartedAtISO,
        endedAt: uploadEndedAtISO,
      },
    },
  };
}

export function makeExtraUploadMetadata(metadata) {
  if (!metadata) {
    return '';
  }
  return Object.keys(metadata)
    .map((key) => `${key} ${btoa(metadata[key])}`)
    .join(',');
}

export const defaultParallelUploadChunkCount = 6;
export const UploadInstanceTus = ({
  id,
  isQueued,
  file,
  onDone,
  onSuccess,
  onTusError,
  onCreationError,
  onFileUploaded,
  onProgress,
  uploadMetadata,
  projectUUID,
  ...rest
}) => {
  const [error, setError] = useState(null);
  const hasStartedUploading = useRef(false);
  const initialBytesUploaded = useRef(0);
  const isResumingUpload = useRef(false);
  const progressBarHackForParallelUploads = useRef(0);
  const name = stringHelpers.stripExtension(file.name);
  const client = useApolloClient();
  const [createUploadDuration] = useMutation(CREATE_UPLOAD_DURATION);
  const { uploadUrl, uploadHeader } = useUploadInfo(projectUUID);
  const isRecording = uploadMetadata?.is_recording;
  const encodedMetadata = makeExtraUploadMetadata({
    ...uploadMetadata,
    project_id: uploadMetadata?.projectId || '__none__',
  });
  if (file.size > 26843545600) {
    throw new Error('File is too large to upload.');
  }

  const parallelUploadChunkCount = defaultParallelUploadChunkCount;
  const arbitraryNumberToGetResumableProgressWorking = 10;

  let bigFile = false;
  if (file && file.size >= 5 * 1024 * 1024 * 1024) {
    bigFile = true;
  }

  useEffect(() => {
    const isReadyToUpload = uploadHeader && uploadUrl;
    if (!file || isQueued || !isReadyToUpload) {
      return;
    }
    if (hasStartedUploading.current) {
      return;
    }
    hasStartedUploading.current = true;
    const headers = {
      Authorization: uploadHeader,
      ExtraUploadMetadata: encodedMetadata,
    };

    const uploadStartedAt = Date.now();
    const upload = new tus.Upload(file, {
      endpoint: uploadUrl,
      headers,
      retryDelays: [0, 3000, 5000, 10000, 20000],
      metadata: {
        ...uploadMetadata,
        filename: name,
        filetype: file.type,
      },
      // eslint-disable-next-line require-await
      fingerprint: async () => {
        return md5(file.name + file.size + file.type);
      },
      parallelUploads: parallelUploadChunkCount,
      removeFingerprintOnSuccess: true,
      onError(err) {
        setError(err);
        onTusError({ id, err });
        onDone({ id });
      },
      onProgress(bytesUploaded, bytesTotal) {
        // If we're resuming and haven't captured the initial bytes yet, capture them now
        if (
          isResumingUpload.current &&
          progressBarHackForParallelUploads.current < arbitraryNumberToGetResumableProgressWorking
        ) {
          // This is a hack.:le-sigh:
          // When we resume an upload, the tus-js-client doesn't tell how many bytes were uploaded before resuming. For
          // non-parallelized uploads, we can just grab the first bytesUploaded value and use that as the initialBytesUploaded.
          // Parallel uploads, on the other hand, are special. Because we have several parallel uploads happening at the same
          // time, we don't get an accurate size of the already uploaded bytes until several progress callbacks have been called.
          // This isn't perfect and there are some edge cases where it might not work as expected.
          progressBarHackForParallelUploads.current += 1;
          initialBytesUploaded.current = bytesUploaded;
        }

        const speed = getSpeed(uploadStartedAt, bytesUploaded, initialBytesUploaded.current);
        const timeRemaining = getETA(
          uploadStartedAt,
          bytesUploaded,
          bytesTotal,
          initialBytesUploaded.current,
        );
        const percentageDone = bytesUploaded / bytesTotal;
        onProgress(id, { percentageDone, speed, timeRemaining });
      },
      onSuccess(payload) {
        const { lastResponse } = payload;
        const inner = async () => {
          const fileId = upload.url.match(/^.*files\/([^+]{32})/)[1];
          const fileExt = getExtensionByFileType(file);
          const uploadedFileUrl = `https://${sslEmbedHost}/deliveries/${fileId}.${fileExt}`;

          const response = await onFileUploaded(client, {
            uploadId: id,
            bakeryHashedId: fileId,
            name,
            uploadedFileUrl,
            ...rest,
          });

          if (response.status === 'success') {
            const uploadEndedAt = Date.now();

            onDone({
              id,
              attributes: response.attributes,
            });
            delete CURRENT_UPLOAD_LIST[id];

            // Performance monitoring
            const totalUploadTimeMilliseconds = getTotalUploadTimeInMilliseconds(
              uploadStartedAt,
              uploadEndedAt,
            );
            const totalUploadTimeSeconds = totalUploadTimeMilliseconds / 1000;
            let mbPerSecond = 0;
            if (initialBytesUploaded.current > 0) {
              mbPerSecond = getMbPerSecond(
                file.size - initialBytesUploaded.current,
                totalUploadTimeSeconds,
              );
            } else {
              mbPerSecond = getMbPerSecond(file.size, totalUploadTimeSeconds);
            }
            const tags = [
              `parallel_upload_count:${parallelUploadChunkCount}`,
              `big_file:${bigFile}`,
            ];
            await distribution('parallel_uploads.time_to_upload', totalUploadTimeSeconds, tags);
            await distribution('parallel_uploads.mb_per_sec', mbPerSecond, tags);
            await distribution('parallel_uploads.file_size_bytes', file.size, tags);

            if (!isRecording) {
              const params = createUploadDurationParams(
                uploadStartedAt,
                uploadEndedAt,
                lastResponse,
                fileId,
                parallelUploadChunkCount,
                totalUploadTimeMilliseconds,
              );
              await createUploadDuration(params);
            }
            onSuccess({ id });
          } else {
            onCreationError({ id, error: response.error });
            onDone({ id });
          }
        };

        inner();
      },
    });

    // Check if there are any previous uploads to continue.
    upload.findPreviousUploads().then((previousUploads) => {
      // Found previous uploads so we select the first one.
      if (previousUploads.length) {
        upload.resumeFromPreviousUpload(previousUploads[0]);
        // Mark that we're resuming an upload
        isResumingUpload.current = true;
      }

      // Start the upload
      upload.start();
      CURRENT_UPLOAD_LIST[id] = upload;
    });
  }, [
    client,
    error,
    id,
    file,
    name,
    onCreationError,
    onDone,
    onFileUploaded,
    onProgress,
    onSuccess,
    rest,
    uploadHeader,
    uploadUrl,
    onTusError,
    isQueued,
    uploadMetadata,
    encodedMetadata,
    parallelUploadChunkCount,
    bigFile,
    createUploadDuration,
    isRecording,
    initialBytesUploaded,
  ]);
  // track that upload is happening
  const { addUnsavedChanges, deleteUnsavedChanges } = useCrossRouteUnsavedChanges();
  const ref = useRef({});
  useEffect(() => {
    const currentRef = ref.current;
    addUnsavedChanges(currentRef);
    return () => {
      deleteUnsavedChanges(currentRef);
    };
  }, [addUnsavedChanges, deleteUnsavedChanges]);
  return null;
};
